import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { theme, card, btnPrimary } from "../theme";
import { apiRequest, ApiError } from "../api/client";
import { msalInstance, loginRequest } from "../auth/msalConfig";

interface Consultancy {
  id: string;
  name: string;
  contactEmail: string | null;
  website: string | null;
}

interface ReleaseInfo {
  orgName: string;
  releasedAt: string;
  consultancies: Consultancy[];
}

type Step = "loading" | "error" | "choose" | "transfer-confirm" | "delete-confirm" | "sign-in-required" | "done-transfer" | "done-delete";

const API = import.meta.env.VITE_API_BASE_URL ?? "";

const RELEASE_PREFIX = "/release/";

/** The token from a /release/:token URL when the page is rendered outside a matching <Route>. */
function tokenFromPathname(pathname: string): string | undefined {
  if (!pathname.startsWith(RELEASE_PREFIX)) return undefined;
  const raw = pathname.slice(RELEASE_PREFIX.length).split("/")[0] ?? "";
  if (!raw) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function ClientReleasePage() {
  const params = useParams<{ token: string }>();
  const location = useLocation();
  // App.tsx renders this page by pathname rather than through a <Route>, so
  // useParams is empty there; fall back to reading the token off the path.
  const token = params.token || tokenFromPathname(location.pathname);
  const [step, setStep] = useState<Step>("loading");
  const [info, setInfo] = useState<ReleaseInfo | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) { setStep("error"); setErrorMsg("Missing token."); return; }
    // Token travels in the body, not the URL, so it never lands in API request logs.
    fetch(`${API}/release/lookup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(async (r) => {
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error ?? "Not found"); }
        return r.json() as Promise<ReleaseInfo>;
      })
      .then((d) => { setInfo(d); setStep("choose"); })
      .catch((e: Error) => { setStep("error"); setErrorMsg(e.message); });
  }, [token]);

  // Acting on the link (transfer or delete) requires a signed-in admin of the
  // organisation on top of the token — a forwarded email must not be enough to
  // hand the data to another firm or erase it.
  async function submitChoice(action: "transfer" | "delete") {
    if (!token || submitting) return;
    setSubmitting(true);
    setErrorMsg("");
    try {
      const body: Record<string, string> = { action, token };
      if (action === "transfer") body.consultancyId = selectedId;
      await apiRequest("/release/choose", { method: "POST", body });
      setStep(action === "transfer" ? "done-transfer" : "done-delete");
    } catch (e: unknown) {
      if (e instanceof ApiError && (e.status === 401 || e.code === "org_admin_required")) {
        setStep("sign-in-required");
        return;
      }
      setErrorMsg(e instanceof ApiError ? (e.code ?? e.message) : e instanceof Error ? e.message : "Something went wrong");
      setStep("choose");
    } finally {
      setSubmitting(false);
    }
  }

  function signIn() {
    // Come back to this exact page once Microsoft has signed the user in.
    void msalInstance.loginRedirect({ ...loginRequest, redirectStartPage: window.location.href });
  }

  const selectedConsultancy = info?.consultancies.find((c) => c.id === selectedId);

  return (
    <div style={{
      minHeight: "100vh", background: theme.color.bg, fontFamily: theme.font,
      display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px",
    }}>
      <div style={{ width: "100%", maxWidth: 560 }}>

        {/* Header */}
        <div style={{ marginBottom: 28, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: theme.color.heading, letterSpacing: "-0.02em" }}>
            Abacas Security Compliance Platform
          </div>
        </div>

        {/* Loading */}
        {step === "loading" && (
          <div style={{ ...card, textAlign: "center", padding: 40, color: theme.color.subtle, fontSize: 13 }}>
            Loading…
          </div>
        )}

        {/* Error */}
        {step === "error" && (
          <div style={{ ...card, borderLeft: `3px solid ${theme.color.danger}` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>Link not found</div>
            <div style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
              {errorMsg || "This link is invalid or has already been used."}
              {" "}If you need help, contact{" "}
              <a href="mailto:support@abacassecurity.com" style={{ color: theme.color.accentText }}>support@abacassecurity.com</a>.
            </div>
          </div>
        )}

        {/* Choose */}
        {step === "choose" && info && (
          <>
            <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.accent}` }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>
                Your consultancy has been removed
              </div>
              <div style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
                <strong>{info.orgName}</strong>'s compliance management was previously handled by a consultancy that
                is no longer on the platform. Your data is safe. Please choose what you'd like to do next.
              </div>
            </div>

            {/* Option A: Transfer */}
            <div style={{ ...card, marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
                Option A — Transfer to a new consultancy
              </div>
              <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14, lineHeight: 1.5 }}>
                Choose a consultancy from the list below. They will be able to view and manage your compliance data.
              </div>

              {info.consultancies.length === 0 ? (
                <div style={{ fontSize: 13, color: theme.color.faint }}>No other consultancies are currently on the platform.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                  {info.consultancies.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedId(c.id)}
                      style={{
                        textAlign: "left", padding: "10px 14px", borderRadius: 3, cursor: "pointer",
                        border: `1.5px solid ${selectedId === c.id ? theme.color.accent : theme.color.border}`,
                        background: selectedId === c.id ? theme.color.accentBg : theme.color.panelAlt,
                        transition: "border-color 0.15s, background 0.15s",
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{c.name}</div>
                      {c.contactEmail && (
                        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{c.contactEmail}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              <button
                disabled={!selectedId}
                onClick={() => setStep("transfer-confirm")}
                style={{ ...btnPrimary, opacity: selectedId ? 1 : 0.4 }}
              >
                Transfer to {selectedConsultancy?.name ?? "selected consultancy"}
              </button>
            </div>

            {/* Option B: Delete */}
            <div style={{ ...card, borderLeft: `3px solid ${theme.color.danger}` }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
                Option B — Delete all my data
              </div>
              <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 14, lineHeight: 1.5 }}>
                Permanently deletes your organisation, all compliance records, documents, and user accounts.
                This cannot be undone.
              </div>
              <button
                onClick={() => setStep("delete-confirm")}
                style={{
                  padding: "8px 18px", borderRadius: 3, fontSize: 13, fontWeight: 600,
                  background: "transparent", border: `1.5px solid ${theme.color.danger}`,
                  color: theme.color.dangerText, cursor: "pointer",
                }}
              >
                Delete all data
              </button>
            </div>

            {errorMsg && (
              <div style={{ marginTop: 12, fontSize: 12, color: theme.color.dangerText }}>{errorMsg}</div>
            )}
          </>
        )}

        {/* Transfer confirmation */}
        {step === "transfer-confirm" && info && selectedConsultancy && (
          <div style={{ ...card }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>
              Confirm transfer
            </div>
            <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 20, lineHeight: 1.6 }}>
              <strong>{info.orgName}</strong> will be transferred to <strong>{selectedConsultancy.name}</strong>.
              They will have full access to your compliance data going forward.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep("choose")} style={{ padding: "8px 18px", borderRadius: 3, fontSize: 13, background: "transparent", border: `1px solid ${theme.color.border}`, color: theme.color.muted, cursor: "pointer" }}>
                Back
              </button>
              <button
                onClick={() => submitChoice("transfer")}
                disabled={submitting}
                style={{ ...btnPrimary, opacity: submitting ? 0.7 : 1 }}
              >
                {submitting ? "Transferring…" : "Confirm transfer"}
              </button>
            </div>
          </div>
        )}

        {/* Delete confirmation */}
        {step === "delete-confirm" && info && (
          <div style={{ ...card, borderLeft: `3px solid ${theme.color.danger}` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>
              Permanently delete all data?
            </div>
            <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 8, lineHeight: 1.6 }}>
              This will permanently delete <strong>{info.orgName}</strong> and everything associated with it:
            </div>
            <ul style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 20, paddingLeft: 20, lineHeight: 2 }}>
              <li>All compliance check results and AI guidance</li>
              <li>Statement of Applicability entries</li>
              <li>All uploaded documents</li>
              <li>Audit log entries</li>
              <li>All user accounts for this organisation</li>
            </ul>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.dangerText, marginBottom: 20 }}>
              This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep("choose")} style={{ padding: "8px 18px", borderRadius: 3, fontSize: 13, background: "transparent", border: `1px solid ${theme.color.border}`, color: theme.color.muted, cursor: "pointer" }}>
                Cancel
              </button>
              <button
                onClick={() => submitChoice("delete")}
                disabled={submitting}
                style={{
                  padding: "8px 18px", borderRadius: 3, fontSize: 13, fontWeight: 600,
                  background: theme.color.danger, color: "#fff", border: "none",
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? "Deleting…" : "Permanently delete all data"}
              </button>
            </div>
          </div>
        )}

        {/* Sign-in required to act on the link */}
        {step === "sign-in-required" && info && (
          <div style={{ ...card, borderLeft: `3px solid ${theme.color.accent}` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>
              Sign in to confirm
            </div>
            <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 20, lineHeight: 1.6 }}>
              For your protection, this choice can only be made by a signed-in administrator of{" "}
              <strong>{info.orgName}</strong>. Sign in with the Microsoft account you use for Abacas and you will be
              brought back here.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep("choose")} style={{ padding: "8px 18px", borderRadius: 3, fontSize: 13, background: "transparent", border: `1px solid ${theme.color.border}`, color: theme.color.muted, cursor: "pointer" }}>
                Back
              </button>
              <button onClick={signIn} style={btnPrimary}>Sign in with Microsoft</button>
            </div>
          </div>
        )}

        {/* Done: transferred */}
        {step === "done-transfer" && info && (
          <div style={{ ...card, textAlign: "center", padding: 40 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>Transfer complete</div>
            <div style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
              <strong>{info.orgName}</strong> has been transferred to <strong>{selectedConsultancy?.name}</strong>.
              Your new consultancy will be in touch to continue managing your compliance.
            </div>
          </div>
        )}

        {/* Done: deleted */}
        {step === "done-delete" && (
          <div style={{ ...card, textAlign: "center", padding: 40 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>Data deleted</div>
            <div style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
              Your organisation and all associated data has been permanently removed from the Abacas Security Compliance Platform.
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
